import {Page} from "../page"

export const userProfiles: Page = {
    uri: 'learn/user-profiles',
    icon: '👤',
    title: $localize`:@@page-user-profiles-title:User Profiles`,
    seoTitle: $localize`:@@page-user-profiles-seo-title:Board gamer profiles — layout, badges and what others see`,
    summary: $localize`:@@page-user-profiles-summary:Your identity on Ludoya — the games you own, the plays you've logged, your badge showcase and stats, arranged the way you want them.`,
    markdownContent: [
        $localize`:@@page-user-profiles-profile-header:Your profile is your identity on Ludoya. It shows what games you own, how often you play, who you follow, and what [groups](/learn/groups) you're in. Other players use your profile to find you, follow you, and coordinate game nights.

## Profile Header

At the top of your profile, you'll see:

- **Cover**: a wide band behind your avatar. Upload a picture of your own, or leave it and Ludoya fills it with your [favourite colour](/learn/live-sessions#page-live-sessions-setting-up-your-session) if you picked one in **Edit profile**, or else a shade of your avatar's colour. [Groups and organizations](/learn/groups) have one too
- **Avatar**: Your profile picture
- **Display Name**: How others know you (can be your real name or a handle)
- **[Premium](/premium) Badge**: A badge if you have an active subscription
- **Badge Showcase**: Up to 3 [achievement badges](/learn/replay-badges) you pick to headline your profile, and a [club badge](/learn/replay-badges) as a crest on your avatar if you wear one
- **Username**: Your unique username (used for mentions and links)
- **Bio**: A short description of yourself with links if you like
- **Location** (optional): Where you're based (shows a map)

Your header is visible to everyone (unless your profile is private).

![A profile's cover band in the colour of its avatar, above the name and follow button](/assets/screenshots/user-profiles-cover.webp)

![Profile header: avatar, name, the three showcase badges, bio, follower counts and location](/assets/screenshots/user-profiles-profile.webp)`,

        $localize`:@@page-user-profiles-following-system:## Following System

Ludoya uses a **one-directional following system**:

- **Follow** someone to see their posts, plays, and activity in your [Following feed](/learn/social-feeds)
- **Followers** are people who follow you
- **Friends** are mutual connections (you follow them, they follow you)

Tap **Follow** on any profile to add them to your Following feed. Tap **Following** to unfollow.

![The Following / Followers list: two tabs, a search filter, and follow, unfollow or block on each person](/assets/screenshots/user-profiles-header.webp)

### Following & Followers

Tap the counters to open a two-tab list — **Following** and **Followers**. From there you can:

- View their profiles
- Follow or unfollow without leaving the list
- Search for a specific person
- Block someone, if it comes to that

### Attendance Reliability Rate

If a player has a no-show rate above 5%, an attendance reliability badge appears on their profile (color-coded from red to yellow). This helps organizers and groups know who's likely to actually show up to [events](/learn/events).`,

        $localize`:@@page-user-profiles-customizable-profile-layout:## Customizable Profile Layout

![Profile layout editor with drag-and-drop section reordering](/assets/screenshots/user-profiles-layout-editor.webp)

You can drag-and-drop profile sections to customize what people see and in what order. Choose from:

- **Storefront** (for shops): what you sell
- **Created Games** (if you're a designer/artist/publisher)
- **[Collection](/learn/collection)**: Your owned games
- **Plays**: Recent games you've played
- **[Statistics](/learn/statistics)**: Your play statistics and trends
- **Taste in games**: Your taste as a star (see below)
- **Communities**: Groups you belong to
- **Events**: Upcoming and past events you've attended
- **[Marketplace](/learn/marketplace)**: Games you're selling
- **Club market** (for clubs): the members' own [market](/learn/marketplace)
- **[Game Kits](/learn/game-kits)**: Game kits you've created
- **[Campaigns](/learn/campaigns)**: Active campaigns you're running

### Toggle Visibility & Reorder

For each section, you can:

- **Toggle on/off**: Hide a section completely
- **Reorder**: Drag sections to change their order
- **Save or reset**: Save your layout or reset to defaults

### Hiding vs. Privacy

Toggling a section off in the layout editor changes **your** profile's layout — it's about presentation, not permissions.

Real visibility controls exist for three areas: your **collection**, your **plays** and your **stats**, each of which can be set to Anyone, Friends, People you share a group with, or Hidden under **Settings → Privacy**. If you want something genuinely restricted rather than just tucked away, that's where to do it. See [Privacy & Visibility](/learn/privacy-visibility).`,

        $localize`:@@page-user-profiles-taste-in-games:## Taste in Games

Answer ten quick questions about the games you enjoy, each a slider between two ends, with a couple of well-known games at each end to show what it means:

- **Weight**: light (Uno, Dixit) or heavy (Terraforming Mars, Brass)
- **Co-op or competitive**: together against the game, or against each other
- **Length**: quick or long
- **Players**: solo or two, or a big group
- **Classic or new**: proven favourites or the latest releases
- **Interaction**: take-that and direct conflict, or peaceful, each building their own thing
- **Luck or skill**
- **Abstract or thematic**: pieces and patterns, or a world to be in
- **Story or systems**: living a story, or mastering how the game works
- **Campaign or one-shot**: a story over many sessions, or a fresh game each time

Set them in **Settings → Taste in games**, with **Find out** in the **What kind of player are you?** notice on your own profile, or from the **Set your taste in games** first step. Every change saves itself. Skip any that don't matter to you with **Skip this one**. Answer at least four to be compared with other players.

Your taste gets a title from your two strongest leanings, like **The Fellowship Gamer** (Thematic and Co-op) or **The Engine Tuner** (Heavy and Systems). On your own profile the card is one line: your title and the ends you lean to hardest (**Heavy**, **Skill**…; fewer on a narrow screen). On another player's, once you have both set yours, the header says how alike you are (for example **54% alike**), and two sliders follow: where you are alike (**=**: close answers, or both leaning the same way) and where you are furthest apart (**≠**), their answer a filled dot and yours a ring. With fewer than four answers in common there is no number, because it would mean nothing. Tap the card to open it in full: the title, how alike you are and your own title, every question they answered as the same kind of slider (with yours on each when you can be compared, marked the same way), each with a **?** that says what the question asks and gives example games for each end, what doesn't matter to them, and **Show as a star** for the same answers as a star. In **Explore → People**, each player whose taste you can compare shows the same **% alike**, and sorting by **Taste similarity** puts the most alike first.

Until you set your taste, your own profile shows a **What kind of player are you?** notice in its place, with a **Find out** button; its **✕** hides it, and **Customize** brings it back. Setting your taste is also one of your **first steps** on your home.

It is what you say, not something worked out from your plays, so you can change it any time. **Recommended for you** in [Explore → Games](/learn/search-discovery) ranks its picks by it, from the next time you open it. Under your own taste, on its page and in Settings, **Games that fit your taste** takes you there, and **People who match you** opens [Explore → People](/learn/search-discovery) sorted by taste, once you have answered four questions. A player whose profile you cannot see shows you no taste either.`,

        $localize`:@@page-user-profiles-section-previews:## Section Previews

![Collection, plays and stats widgets stacked on a profile](/assets/screenshots/user-profiles-widgets.webp)

Sections stack down the page in whatever order you've set, each showing a summary with an arrow through to the full thing. Here's what shows in each section:

### Collection

All games you own. Shows owner vs. played counts. Taps through to full collection filtering.

### Recent Plays

The last 5-10 games you've logged with scores and photos. Shows an "unconfirmed" count if plays are awaiting confirmation from other players. Only the profile owner sees unconfirmed plays on their profile.

### Stats

A snapshot of your [statistics](/learn/statistics): plays, time, wins, games, players and locations, plus three highlights — your **top game**, your **top partner** (the person you play with most), and your **H-index**.

### Groups

All groups you're a member of. Tap to jump to a group's page.

### Upcoming Events

Events you've RSVP'd to attend, sorted by date.

### Marketplace

Games you're selling or trading. Tap to see listing details.

### Game Kits

Game kits you've designed. Shows the kit name and games included.

### Active Campaigns

Campaigns you're running. Shows participants and progress.

### Activity Feed

All your recent posts, play logs, and interactions. Infinite scroll to see your history.`,

        $localize`:@@page-user-profiles-tips:## Tips on a Profile

Signed in, **Show me around** at the bottom of a profile's ⋮ menu walks you through it, one control at a time. On yours: **Edit Profile**, your taste in games, your collection, plays and stats, and **Customize Profile**. On someone else's: **Follow**, writing to them, and what they share. The tips follow the cards in the order the profile shows them, and the page offers them the first few times you open one.`,

        $localize`:@@page-user-profiles-editing-your-profile:## Editing Your Profile

Tap **Edit Profile** to update:

- **Display Name**: How you appear to others
- **Bio**: Tell people about yourself (you can include links)
- **Avatar**: Upload a new profile picture
- **Cover**: use the button on the band itself to upload one, crop it, or remove it
- **Location**: Set your location using a map picker
- **Date of birth**, **Gender** and **Phone number**: never shown on your profile. They're there so an event's [sign-up form](/learn/event-operations) that asks for them can be filled in for you

You can also reach **Get Premium** or **Manage Premium**, and **Convert into Organization** ([what that means](/learn/organizations)), from Edit Profile.`,

        $localize`:@@page-user-profiles-named-players-players-without-a-ludoya:## Named Players (Players Without a Ludoya Account)

When you log plays, not every person at the table will have a Ludoya account. For anyone who doesn't, you add them as a **named player** — a lightweight entry that's just a name (and optionally an avatar) attached to your play history. Named players let you keep a complete record of who you played with, even when half the table isn't on Ludoya.

Named players are **yours**: you created them, they belong to your play history, and they're distinct from full Ludoya user accounts. A Ludoya user with their own login is never a "player" in this sense — the two concepts are separate.

### Linking a Named Player to a Real Ludoya Account

If a friend you've been logging as a named player later joins Ludoya, you can link the named player to their new account:

1. Tap the named player entry in your play history
2. Tap **Link**
3. Search for their Ludoya username or share your invite link/QR code for them to accept

Once linked, the past plays you logged with the named player get credited to their real account, so their profile reflects the full shared history.

### Linking a Ludoya User to a Named Player

The reverse direction applies. For example, when you import play history from **BoardGameGeek**, your BGG plays come in with the named players you used over there. If one of those imported named players turns out to be a real Ludoya user, you can link the Ludoya user **to** the imported named player, merging the imported BGG history into their account.

### Merging Duplicate Named Players

If you've created multiple named players for the same person (e.g., "Alex" and "Alexander"), you can merge them:

1. Go to one named player entry → **Merge**
2. Select the duplicate
3. Confirm — all plays consolidate under a single named player`,

        $localize`:@@page-user-profiles-creator-profiles:## Creator Profiles

If you've designed games or created artwork, Ludoya recognizes you as a creator:

- **Designers** get a dedicated "Created Games" section
- **Artists** get listed as artists on game pages
- **Publishers** get their own game page linking all published titles

Tap your profile to see all games you've created—great for showcasing your work.`,

        $localize`:@@page-user-profiles-privacy-settings:## Privacy Settings

Your profile, collection, plays and stats each have a visibility setting. See [Privacy & Visibility](/learn/privacy-visibility) for the full breakdown, but here's the quick version:

- **Anyone** — public, everyone sees it
- **Friends** — only people you follow who follow you back
- **People I share group with** — only your group mates
- **Hidden** — only you

Collection, plays and stats can never be more open than your profile itself.

### Blocking & Reporting

You can:

- **Block a user**: Hide their content from you and yours from them. Both are removed from follow lists.
- **Report a user**: Flag inappropriate behavior for the Ludoya team to review.

### Auto-Accept Plays

This is set **per person**, not globally. When someone tags you in a play, you can tick the option to always add that person's plays in future — ideal for the people you play with every week. To switch it off again, open their profile. See [play tags](/learn/play-logging).`,

        $localize`:@@page-user-profiles-section-9:**See also:**
- [Privacy & Visibility](/learn/privacy-visibility) — control who sees your profile and data
- [Social & Feeds](/learn/social-feeds) — activity streams and discovery
- [Play Logging](/learn/play-logging) — recording games you've played
- [Statistics](/learn/statistics) — your gaming data and trends
- [Organizations](/learn/organizations) — creating a dedicated account`,
    ].join('\n\n---\n\n')
}
